import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form, Badge } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { Authorization } from "../Context/Authorization";
import Preloader from "../shared/PreLoaders";

const CircleStatusModal = ({ open, onClose, Api }) => {
  const [month, setMonth] = useState(() => {
    const today = new Date();
    today.setMonth(today.getMonth() - 1);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  });

  const [circleData, setCircleData] = useState([]);
  const [stateName, setStateName] = useState("");
  const [loading, setLoading] = useState(false);
  const [jfmcTypes, setJfmcTypes] = useState([]);
  const [selectedJfmcTypeId, setSelectedJfmcTypeId] = useState(null);

  const fetchCircleStatus = async () => {
    try {
      setLoading(true);
      const config = Authorization();

      const response = await axios.get(
        `${Api}/api/declaration/state/status`,
        { ...config, params: { month } }
      );

      const circles = response.data.circles || [];

      setCircleData(circles);
      setStateName(response.data.stateName || "Unknown State");

      // 🔥 Extract unique JFMC types
      const allTypes = circles.flatMap(c => c.jfmcTypes || []);
      const uniqueMap = {};

      allTypes.forEach(type => {
        if (!uniqueMap[type.jfmcTypeId]) {
          uniqueMap[type.jfmcTypeId] = {
            jfmcTypeId: type.jfmcTypeId,
            jfmcTypeName: type.jfmcTypeName,
          };
        }
      });

      const uniqueTypes = Object.values(uniqueMap);
      setJfmcTypes(uniqueTypes);

      if (uniqueTypes.length > 0) {
        setSelectedJfmcTypeId(uniqueTypes[0].jfmcTypeId);
      }

    } catch (err) {
      console.error("Error fetching circle status:", err);
      toast.error("Failed to load circle status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchCircleStatus();
  }, [open, month]);

  // 🔥 Filter circles based on selected JFMC
  const filteredCircles = circleData.filter(circle =>
    circle.jfmcTypes?.some(
      t => t.jfmcTypeId === selectedJfmcTypeId
    )
  );

  const totalCircles = filteredCircles.length;

  const completedCircles = filteredCircles.filter(circle => {
    const type = circle.jfmcTypes.find(
      t => t.jfmcTypeId === selectedJfmcTypeId
    );
    return type?.allVfcsSubmitted;
  }).length;

  return (
    <Modal show={open} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          State View {stateName ? `- ${stateName}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>

        {/* Month */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Form.Group>
            <Form.Label>Select Month</Form.Label>
            <Form.Control
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{ width: "200px" }}
            />
          </Form.Group>

          <div>
            <strong>Completed:</strong> {completedCircles} / {totalCircles}
          </div>
        </div>

        {/* 🔘 JFMC TYPE BUTTONS */}
        {jfmcTypes.length > 0 && (
          <div className="d-flex gap-2 mb-3 justify-content-center">
            {jfmcTypes.map((type) => (
              <div className="jfmc-switch">
                <button
                  className={`btn ${selectedJfmcTypeId?.jfmcTypeId === type.jfmcTypeId ? "btn-success" : "btn-outline-secondary"}`}
                  key={type.jfmcTypeId}
                  
                  onClick={() => setSelectedJfmcTypeId(type.jfmcTypeId)}
                >
                  {type.jfmcTypeName}
                </button>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center my-4">
            <Preloader loading={loading} />
          </div>
        ) : (
          <Table bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Circle Name</th>
                <th>VFC Progress</th>
                <th>Status</th>
                <th>Submitted Date</th> {/* ✅ NEW COLUMN */}
              </tr>
            </thead>
            <tbody>
              {filteredCircles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No circles found
                  </td>
                </tr>
              ) : (
                filteredCircles
                  .sort((a, b) =>
                    a.circleName.localeCompare(b.circleName)
                  )
                  .map((circle, index) => {

                    const selectedType = circle.jfmcTypes.find(
                      t => t.jfmcTypeId === selectedJfmcTypeId
                    );

                    const submittedDate = selectedType?.submittedAt;

                    return (
                      <tr key={circle.circleId}>
                        <td>{index + 1}</td>
                        <td>{circle.circleName}</td>

                        <td>
                          {selectedType?.submittedVfcs || 0} /{" "}
                          {selectedType?.totalVfcs || 0}
                        </td>

                        <td>
                          <Badge
                            bg={
                              selectedType?.allVfcsSubmitted
                                ? "success"
                                : "warning"
                            }
                          >
                            {selectedType?.allVfcsSubmitted
                              ? "Completed"
                              : "Pending"}
                          </Badge>
                        </td>

                        {/* ✅ SHOW DATE OR "-" */}
                        <td>
                          {submittedDate
                            ? new Date(submittedDate).toLocaleString()
                            : "-"}
                        </td>

                      </tr>
                    );
                  })
              )}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CircleStatusModal;